import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import MovieCreationOutlinedIcon from "@mui/icons-material/MovieCreationOutlined";
import MusicNoteOutlinedIcon from "@mui/icons-material/MusicNoteOutlined";
import ClosedCaptionOutlinedIcon from "@mui/icons-material/ClosedCaptionOutlined";
import { useTranslationApp } from "../../lib/i18next";
import {
  MODAL_VIDEO_EDITOR_ADD_SUBTITLES,
  MODAL_VIDEO_EDITOR_TRACK_AUDIO,
  MODAL_VIDEO_EDITOR_TRACK_IMAGE,
  MODAL_VIDEO_EDITOR_TRACK_VIDEO,
} from "../../i18n/keysTranslations";
import {
  getTimelineRowHeight,
  VIDEO_EDITOR_LABEL_COLOR,
  VIDEO_EDITOR_TIMELINE_ROW_BG,
  VIDEO_EDITOR_TRACK_TYPE,
} from "./constants";

const TRACK_ADD_CONFIG = {
  [VIDEO_EDITOR_TRACK_TYPE.IMAGE]: {
    labelKey: MODAL_VIDEO_EDITOR_TRACK_IMAGE,
    Icon: ImageOutlinedIcon,
    compactSize: 24,
  },
  [VIDEO_EDITOR_TRACK_TYPE.VIDEO]: {
    labelKey: MODAL_VIDEO_EDITOR_TRACK_VIDEO,
    Icon: MovieCreationOutlinedIcon,
    compactSize: 32,
  },
  [VIDEO_EDITOR_TRACK_TYPE.AUDIO]: {
    labelKey: MODAL_VIDEO_EDITOR_TRACK_AUDIO,
    Icon: MusicNoteOutlinedIcon,
    compactSize: 24,
  },
  [VIDEO_EDITOR_TRACK_TYPE.SUBTITLE]: {
    labelKey: MODAL_VIDEO_EDITOR_ADD_SUBTITLES,
    Icon: ClosedCaptionOutlinedIcon,
    compactSize: 24,
  },
};

const VideoEditorTimelineTrackAddButtons = ({
  action,
  onAddToTrack = () => {},
}) => {
  const { t } = useTranslationApp();
  const type = action?.data?.type;
  const hasClips = Boolean(action?.data?.hasClips);
  const config = TRACK_ADD_CONFIG[type];

  if (!config) {
    return null;
  }

  const label = t(config.labelKey);
  const Icon = hasClips ? AddIcon : config.Icon;
  const rowHeight = getTimelineRowHeight({ type });
  const buttonHeight = hasClips
    ? config.compactSize
    : Math.max(0, rowHeight - 4);

  const onStopTimelinePointer = (event) => {
    event.stopPropagation();
  };

  return (
    <Box
      component="button"
      type="button"
      data-video-editor-track-add="true"
      aria-label={label}
      title={hasClips ? label : undefined}
      onClick={(event) => {
        event.stopPropagation();
        onAddToTrack({ type });
      }}
      onMouseDown={onStopTimelinePointer}
      onPointerDown={onStopTimelinePointer}
      sx={{
        width: "100%",
        height: buttonHeight,
        mt: `${Math.max(0, (rowHeight - buttonHeight) / 2)}px`,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: hasClips ? 0 : 0.75,
        px: hasClips ? 0 : 1.25,
        border: hasClips
          ? "1px solid #c8cfda"
          : "1px solid transparent",
        cursor: "pointer",
        borderRadius: hasClips ? "7px" : "8px",
        bgcolor: hasClips
          ? "rgba(255,255,255,0.92)"
          : VIDEO_EDITOR_TIMELINE_ROW_BG,
        color: VIDEO_EDITOR_LABEL_COLOR,
        boxShadow: hasClips
          ? "0 1px 3px rgba(32,37,45,0.14)"
          : "none",
        transition:
          "background-color 120ms ease, border-color 120ms ease, color 120ms ease, transform 120ms ease",
        "&:hover": {
          bgcolor: hasClips ? "#f2efff" : "#e4e7ed",
          borderColor: hasClips ? "#9d8cff" : "transparent",
          color: hasClips ? "#5c36ed" : "#4f5c70",
          transform: hasClips ? "scale(1.04)" : "none",
        },
        "&:focus-visible": {
          outline: "2px solid #6e4aff",
          outlineOffset: 1,
        },
      }}
    >
      <Icon
        sx={{
          fontSize:
            hasClips && type === VIDEO_EDITOR_TRACK_TYPE.VIDEO ? 20 : 17,
          flexShrink: 0,
        }}
      />
      {!hasClips ? (
        <Typography
          component="span"
          sx={{
            fontSize: 11,
            fontWeight: 600,
            lineHeight: 1.2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Typography>
      ) : null}
    </Box>
  );
};

export default VideoEditorTimelineTrackAddButtons;
